import React from "react";
import { useTranslate } from "@refinedev/core";
import { Divider, Flex, Popover, Typography, theme } from "antd";
import dayjs from "dayjs";
import { useOpeningStatus } from "../../hooks/tenant";
import {
    DEFAULT_TIMEZONE,
    getTenantNow,
    upcomingClosures,
} from "../../shared/openingHours";
import { TENANT_WEEKDAYS, type TenantWeekday } from "../../models/tenant";

const { Text } = Typography;

/** How many upcoming closures the popover lists before it stops. */
const MAX_LISTED_CLOSURES = 3;

interface OpeningStatusBadgeProps {
    /** Dot + "Ouvert"/"Fermé" only, for the 56px mobile header. */
    compact?: boolean;
    className?: string;
}

/**
 * Live open/closed pill for the customer header, with the full weekly schedule
 * and upcoming closures in a popover. Renders nothing until the tenant has
 * configured a schedule and switched it on.
 */
export const OpeningStatusBadge: React.FC<OpeningStatusBadgeProps> = ({
    compact = false,
    className,
}) => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const { status, openingHours, closingDates } = useOpeningStatus();

    if (!status || !openingHours) {
        return null;
    }

    const timezone = openingHours.timezone || DEFAULT_TIMEZONE;
    const today = getTenantNow(timezone);
    const color = status.isOpen ? token.colorSuccess : token.colorError;

    const dayName = (day: TenantWeekday) => _t(`openingHours.weekdays.${day}`);

    const detail = (): string | null => {
        if (status.isOpen) {
            return status.closesAt
                ? _t("openingHours.closesAt", { time: status.closesAt })
                : null;
        }
        if (!status.nextOpening) return null;
        return status.nextOpening.isToday
            ? _t("openingHours.reopensToday", { time: status.nextOpening.time })
            : _t("openingHours.reopensOn", {
                  day: dayName(status.nextOpening.weekday),
                  time: status.nextOpening.time,
              });
    };

    const label = status.isOpen ? _t("openingHours.open") : _t("openingHours.closed");
    const detailText = detail();

    const formatDate = (date: string) => dayjs(date).format("DD/MM/YYYY");
    const closures = upcomingClosures(closingDates, today.date).slice(
        0,
        MAX_LISTED_CLOSURES,
    );

    const popoverContent = (
        <div style={{ minWidth: 240, maxWidth: 300 }}>
            <Text strong style={{ fontSize: 13 }}>
                {_t("openingHours.weeklySchedule")}
            </Text>
            <div style={{ marginTop: 8 }}>
                {TENANT_WEEKDAYS.map((day) => {
                    const ranges = openingHours.week?.[day] ?? [];
                    const isToday = day === today.weekday;
                    return (
                        <Flex
                            key={day}
                            justify="space-between"
                            gap={16}
                            style={{ fontSize: 12, padding: "2px 0" }}
                        >
                            {/* fr day names are stored lowercase so they read
                                correctly inside a sentence ("réouvre lundi"). */}
                            <span
                                className="capitalize"
                                style={{
                                    fontWeight: isToday ? 600 : 400,
                                    color: isToday ? token.colorText : token.colorTextSecondary,
                                }}
                            >
                                {dayName(day)}
                            </span>
                            <span
                                style={{
                                    textAlign: "right",
                                    fontWeight: isToday ? 600 : 400,
                                    color:
                                        ranges.length === 0
                                            ? token.colorTextTertiary
                                            : token.colorText,
                                }}
                            >
                                {ranges.length === 0
                                    ? _t("openingHours.closedAllDay")
                                    : ranges
                                          .map((range) => `${range.start} - ${range.end}`)
                                          .join(" · ")}
                            </span>
                        </Flex>
                    );
                })}
            </div>

            {closures.length > 0 && (
                <>
                    <Divider style={{ margin: "10px 0" }} />
                    <Text strong style={{ fontSize: 13 }}>
                        {_t("openingHours.upcomingClosures")}
                    </Text>
                    <div style={{ marginTop: 6 }}>
                        {closures.map((closure) => (
                            <Flex
                                key={closure.id}
                                justify="space-between"
                                gap={16}
                                style={{ fontSize: 12, padding: "2px 0" }}
                            >
                                <span style={{ color: token.colorTextSecondary }}>
                                    {closure.label || _t("openingHours.closed")}
                                </span>
                                <span style={{ textAlign: "right" }}>
                                    {closure.startDate === closure.endDate
                                        ? formatDate(closure.startDate)
                                        : `${formatDate(closure.startDate)} - ${formatDate(closure.endDate)}`}
                                </span>
                            </Flex>
                        ))}
                    </div>
                </>
            )}

            <Divider style={{ margin: "10px 0 6px" }} />
            <Text style={{ fontSize: 11, color: token.colorTextTertiary }}>
                {_t("openingHours.timezoneNote", { timezone })}
            </Text>
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            trigger={compact ? "click" : "hover"}
            placement="bottom"
        >
            <Flex
                align="center"
                gap={6}
                className={className}
                style={{ cursor: "pointer", maxHeight: 20 }}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: color,
                        flexShrink: 0,
                    }}
                />
                <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
                {!compact && detailText && (
                    <>
                        <span style={{ opacity: 0.4, fontSize: 12 }}>·</span>
                        <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                            {status.reason === "holiday" && status.activeClosure?.label
                                ? status.activeClosure.label
                                : detailText}
                        </span>
                    </>
                )}
            </Flex>
        </Popover>
    );
};
